import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MainLogo from "../components/Logo";
import CenterSpinner from "../components/CenterSpinner";
import Toast from "../components/Toast";
import StudioPolicyConsent from "../components/StudioPolicyConsent";
import { useAuth } from "../contexts/AuthContext";
import { auth } from "../firebase";
import {
  createAccessRequest,
  getAccessStatus,
  studioPolicyConsent,
  type AccessStatusResponse,
} from "../services/api/access";
import designTokens from "../styles/designTokens";
import { authPrimaryButtonSx } from "../styles/authPrimaryButton";
import {
  precheckEmailPasswordAccount,
  type EmailPasswordPrecheckResponse,
} from "../services/api/auth";
import { getCompanyLabel } from "./accessCompanyLabel";
import {
  isSocialAccessRequest,
  resolveSocialProviderIcon,
} from "./accessLoginKind";
import { resolveAuthProfilePhotoURL } from "../services/authProviderProfile";
import DSDialog from "../components/design-system/DSDialog";
import {
  handleWebViewLoginSuccess,
  isViewRNativeAuthWebView,
  notifyLoginShellTransitionBegin,
  notifyNativeLoginShellState,
} from "../utils/webViewUtils";
import { notifyAuthSuccessToServer } from "../services/webViewService";

const ACCESS_STATUS_COPY = {
  approved: {
    title: "Access approved",
    body: "Your account has been successfully approved.\nContinue to start using View R Studio.",
    chip: "Approved",
  },
  pending: {
    title: "Access request sent",
    body: "Your access request has been sent to the View R.\nYou can continue after this one-time approval.",
    chip: "Awaiting approval",
  },
  rejected: {
    title: "Access request not approved",
    body: "Your request was not approved. Contact your View R administrator if you need access.",
    chip: "Not approved",
  },
  inactive: {
    title: "Account inactive",
    body: "This account is inactive. Contact your View R administrator to restore access.",
    chip: "Inactive",
  },
  error: {
    title: "Access request unavailable",
    body: "This account cannot request Studio access.",
    chip: "",
  },
} as const;

interface EmailFirstAccessState {
  email?: string;
  precheck?: EmailPasswordPrecheckResponse;
  accessStatus?: AccessStatusResponse;
  existingPending?: boolean;
  showApprovalConfirmation?: boolean;
}

const readStoredEmailFirstAccess = (): EmailFirstAccessState => {
  try {
    return JSON.parse(
      sessionStorage.getItem("emailFirstAccess") || "{}",
    ) as EmailFirstAccessState;
  } catch {
    return {};
  }
};

const getAccessRequestError = (requestError: unknown): string => {
  const raw =
    requestError instanceof Error
      ? requestError.message
      : String(requestError ?? "");

  if (raw.includes("company_email_required")) {
    return "Use a company email registered with View R to request access.";
  }
  if (raw.includes("supported_login_required")) {
    return "Use an Email/Password account issued by View R or Google, Microsoft, or Apple to request access.";
  }

  return "Access status could not be checked. Try again.";
};

const AccessRequestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout } = useAuth();
  const isNativeAvpWebView = useMemo(() => isViewRNativeAuthWebView(), []);
  const avpAccessTokens = designTokens.semantic.avpAuthentication.access;
  const initialFlow = useMemo<EmailFirstAccessState>(() => {
    const routeState = (location.state ?? {}) as EmailFirstAccessState;
    return { ...readStoredEmailFirstAccess(), ...routeState };
  }, [location.state]);
  const initialRequestConfirmation =
    initialFlow.precheck?.nextAction === "request_access";
  const [status, setStatus] = useState<AccessStatusResponse | null>(
    initialFlow.accessStatus ?? null,
  );
  const [precheck, setPrecheck] =
    useState<EmailPasswordPrecheckResponse | null>(
      initialFlow.precheck ?? null,
    );
  const [flowEmail, setFlowEmail] = useState(
    initialFlow.email || initialFlow.precheck?.email || "",
  );
  const [existingPending, setExistingPending] = useState(
    Boolean(initialFlow.existingPending),
  );
  const [requestConfirmation, setRequestConfirmation] = useState(
    initialRequestConfirmation,
  );
  const [checking, setChecking] = useState(!initialRequestConfirmation);
  const [requesting, setRequesting] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [error, setError] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [showApprovalConfirmation, setShowApprovalConfirmation] = useState(
    Boolean(initialFlow.showApprovalConfirmation),
  );

  useEffect(() => {
    if (isNativeAvpWebView) notifyNativeLoginShellState("access");
  }, [isNativeAvpWebView]);

  const refresh = async ({
    notifyIfPending = false,
    showApprovedConfirmation = false,
  }: {
    notifyIfPending?: boolean;
    showApprovedConfirmation?: boolean;
  } = {}): Promise<void> => {
    setChecking(true);
    setError("");
    try {
      const hasFirebaseSession = Boolean(user && !user.isGuest && user.token);
      if (hasFirebaseSession) {
        const current = await getAccessStatus();
        const shouldClaimIdentity =
          current.accessStatus === "not_requested" ||
          (["pending", "approved"].includes(current.accessStatus) &&
            current.request?.identityClaimRequired === true);
        const resolved =
          current.permissionScope !== "full" && shouldClaimIdentity
            ? await createAccessRequest()
            : current;
        const shouldShowApprovedConfirmation =
          isNativeAvpWebView ||
          showApprovalConfirmation ||
          showApprovedConfirmation;
        if (
          resolved.accessStatus === "approved" &&
          resolved.permissionScope === "full" &&
          !shouldShowApprovedConfirmation
        ) {
          sessionStorage.removeItem("emailFirstAccess");
          navigate("/home", { replace: true });
          return;
        }
        setStatus(resolved);
        setFlowEmail(resolved.request?.email || user?.email || flowEmail);
        setShowApprovalConfirmation(
          resolved.accessStatus === "approved" &&
            resolved.permissionScope === "full" &&
            shouldShowApprovedConfirmation,
        );
        if (notifyIfPending && resolved.accessStatus === "pending") {
          setToastOpen(true);
        }
        return;
      }

      const normalizedEmail = flowEmail.toLowerCase();
      if (!normalizedEmail) {
        navigate("/login", { replace: true });
        return;
      }
      const checked = await precheckEmailPasswordAccount(normalizedEmail);
      if (checked.nextAction === "request_access") {
        // Admin state가 삭제된 탈퇴 계정이나 운영자가 요청을 삭제한 계정이
        // 과거 /access sessionStorage 때문에 승인 화면에 갇히지 않게 한다.
        sessionStorage.removeItem("emailFirstAccess");
        navigate("/login", {
          replace: true,
          state: { email: normalizedEmail },
        });
        return;
      }
      const wasPending =
        precheck?.requestStatus === "pending" ||
        status?.accessStatus === "pending";
      setPrecheck(checked);
      setStatus(null);
      setShowApprovalConfirmation(
        showApprovedConfirmation &&
          wasPending &&
          checked.requestStatus === "approved",
      );
      sessionStorage.setItem(
        "emailFirstAccess",
        JSON.stringify({
          email: normalizedEmail,
          precheck: checked,
          existingPending,
        }),
      );
      if (notifyIfPending && checked.requestStatus === "pending") {
        setToastOpen(true);
      }
    } catch (requestError) {
      console.error("Access request status check failed:", requestError);
      setError(getAccessRequestError(requestError));
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (requestConfirmation) return;
    if (!loading && user?.isGuest) {
      navigate("/home", { replace: true });
      return;
    }
    if (loading) return;
    if (user && !user.isGuest && user.token) {
      void refresh();
      return;
    }
    if (flowEmail) {
      void refresh();
      return;
    }
    navigate("/login", { replace: true });
    // This is intentionally a login-bound initial fetch. Manual refresh is explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user?.uid]);

  const email =
    status?.request?.email || precheck?.email || flowEmail || user?.email || "";

  const requestAgain = async (): Promise<void> => {
    if (!email) return;
    const isInitialRequest = requestConfirmation;
    if (isInitialRequest) setRequesting(true);
    else setChecking(true);
    setError("");
    try {
      const hasFirebaseSession = Boolean(user && !user.isGuest && user.token);
      const next = await createAccessRequest(
        hasFirebaseSession ? undefined : email.toLowerCase(),
      );
      setStatus(next);
      setPrecheck(null);
      setExistingPending(false);
      setRequestConfirmation(false);
      sessionStorage.setItem(
        "emailFirstAccess",
        JSON.stringify({ email: email.toLowerCase(), accessStatus: next }),
      );
    } catch (requestError) {
      const raw =
        requestError instanceof Error
          ? requestError.message
          : String(requestError ?? "");
      if (raw.includes("request_already_pending")) {
        try {
          const checked = await precheckEmailPasswordAccount(
            email.toLowerCase(),
          );
          setStatus(null);
          setPrecheck(checked);
          setExistingPending(true);
          setRequestConfirmation(false);
          sessionStorage.setItem(
            "emailFirstAccess",
            JSON.stringify({
              email: email.toLowerCase(),
              precheck: checked,
              existingPending: true,
            }),
          );
          return;
        } catch (refreshError) {
          setError(getAccessRequestError(refreshError));
          return;
        }
      }
      setError(
        isInitialRequest
          ? "Access request failed. Try again."
          : getAccessRequestError(requestError),
      );
    } finally {
      if (isInitialRequest) setRequesting(false);
      else setChecking(false);
    }
  };

  if (loading || checking) {
    return (
      <CenterSpinner
        radius={35}
        thickness={10}
        ariaLabel="Checking access"
        speedSec={1.4}
        color="#50B786"
        trackColor="rgba(80, 183, 134, 0.3)"
        arcRatio={0.2}
      />
    );
  }

  const resolvedAccessStatus = status?.accessStatus ?? precheck?.requestStatus;
  const accessState =
    !status && !precheck && error
      ? "error"
      : resolvedAccessStatus === "approved"
        ? "approved"
        : resolvedAccessStatus === "inactive"
          ? "inactive"
          : resolvedAccessStatus === "rejected"
            ? "rejected"
            : "pending";
  const profileInitial = email.trim().charAt(0).toUpperCase() || "U";
  const companyName = getCompanyLabel(
    status?.tenantName ?? precheck?.tenantName,
  );
  const firebaseProviders = [
    ...(auth.currentUser?.providerData ?? []),
    ...(user?.providerData ?? []),
  ];
  const firebaseProviderIds = firebaseProviders.map(
    (provider) => provider.providerId,
  );
  const assignedProvider =
    status?.request?.authProvider ?? precheck?.assignedProvider;
  const isSocialLogin =
    isSocialAccessRequest(status, firebaseProviderIds) ||
    precheck?.authType === "social" ||
    ["google", "microsoft", "apple"].includes(assignedProvider || "");
  const requiresConsent = Boolean(
    user && status?.consentRequired && status.policy,
  );
  const baseCopy = ACCESS_STATUS_COPY[accessState];
  const copy = requestConfirmation
    ? {
        title: "New to View R",
        body: "Request access if this is your first time using View R.\nIf you’ve signed in before, verify your email address.",
        chip: "",
      }
    : accessState === "approved" && requiresConsent
      ? {
          ...baseCopy,
          body: "Your account has been approved.\nAgree to the terms to start using View R Studio.",
        }
      : accessState === "approved" && isSocialLogin
        ? {
            ...baseCopy,
            body: isNativeAvpWebView
              ? "Your account has been successfully approved.\nContinue to start using View R."
              : "Your account has been successfully approved.\nSign in to start using View R Studio.",
          }
        : accessState === "approved" && isNativeAvpWebView
          ? {
              ...baseCopy,
              body: "Your account has been successfully approved.\nContinue to start using View R.",
            }
          : baseCopy;
  // API가 내려주는 provider는 표시 label(`Google`)이고 Firebase는 raw id
  // (`google.com`)라 어휘가 다르다. API 값을 먼저 보되 못 찾으면 Firebase로 잇는다.
  const providerIcon = isSocialLogin
    ? resolveSocialProviderIcon([
        status?.request?.provider,
        assignedProvider,
        ...firebaseProviderIds,
      ])
    : null;
  // Sign in with Apple은 계정 프로필 이미지를 제공하지 않는다. 이전에 저장된
  // photoURL이 남아 있어도 Apple 승인 카드는 회사 계정과 같은 이니셜 썸네일을 쓴다.
  const usesCompanyAccountThumbnail =
    !isSocialLogin || providerIcon?.alt === "Apple";
  const profilePhotoURL = !usesCompanyAccountThumbnail
    ? resolveAuthProfilePhotoURL({
        credentialPhotoURL: user?.photoURL,
        firebasePhotoURL: auth.currentUser?.photoURL,
        providerPhotoURLs: firebaseProviders.map(
          (provider) => provider.photoURL,
        ),
      })
    : null;

  const returnToLogin = (): void => {
    sessionStorage.removeItem("emailFirstAccess");
    if (user) void logout();
    if (isNativeAvpWebView) {
      notifyLoginShellTransitionBegin(
        new URL("/login", window.location.href).href,
      );
    }
    navigate("/login", {
      replace: true,
      state: requestConfirmation ? { email } : undefined,
    });
  };

  const continueAfterApproval = async (): Promise<void> => {
    if (!user && assignedProvider === "password") {
      sessionStorage.removeItem("emailFirstAccess");
      if (isNativeAvpWebView) notifyNativeLoginShellState("create");
      navigate("/login", {
        replace: true,
        state: { email, step: "create", policy: precheck?.policy },
      });
      return;
    }
    if (requiresConsent) {
      if (!policyAccepted || !status?.policy) return;
      setRequesting(true);
      setError("");
      try {
        const activated = await createAccessRequest(
          undefined,
          studioPolicyConsent(status.policy),
        );
        if (activated.permissionScope !== "full") {
          throw new Error("Studio access activation did not complete.");
        }
        setStatus(activated);
      } catch (activationError) {
        console.error("Studio policy consent failed:", activationError);
        let activated = false;
        try {
          const refreshed = await getAccessStatus();
          setStatus(refreshed);
          activated = refreshed.permissionScope === "full";
        } catch {
          // Keep the current approved state when refresh also fails.
        }
        if (!activated) {
          setPolicyAccepted(false);
          setError(
            "Your consent could not be saved. Review the current policy and try again.",
          );
          return;
        }
      } finally {
        setRequesting(false);
      }
    }
    sessionStorage.removeItem("emailFirstAccess");
    if (isNativeAvpWebView && user?.token) {
      const webViewUserInfo = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        provider: assignedProvider || (isSocialLogin ? "social" : "email"),
        token: user.token,
      };
      handleWebViewLoginSuccess(user.token, webViewUserInfo);
      try {
        await notifyAuthSuccessToServer(user.token, webViewUserInfo);
      } catch (notificationError) {
        console.warn(
          "AVP approved access server notification failed:",
          notificationError,
        );
      }
      return;
    }
    navigate(user ? "/home" : "/login", {
      replace: true,
      state: user ? undefined : { email },
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: designTokens.fill.background.grey20.dark,
        color: designTokens.fill.text.grey90.dark,
        px: isNativeAvpWebView ? 0 : { xs: 3, sm: 5 },
        position: "relative",
        overflow: isNativeAvpWebView ? "auto" : undefined,
      }}
    >
      <Box
        sx={{
          height: isNativeAvpWebView ? avpAccessTokens.navigation.height : 98,
          display: "flex",
          alignItems: "center",
          justifyContent: isNativeAvpWebView ? "center" : undefined,
        }}
      >
        {isNativeAvpWebView ? (
          <Typography
            component="h1"
            sx={{
              fontFamily: designTokens.typography.fontFamily.roboto,
              fontSize: 28,
              fontWeight: 700,
              color: designTokens.fill.text.common100.dark,
            }}
          >
            {copy.title}
          </Typography>
        ) : (
          <MainLogo size={48} />
        )}
      </Box>
      {isNativeAvpWebView && accessState !== "approved" && (
        <Button
          aria-label="Back"
          onClick={returnToLogin}
          sx={{
            position: "absolute",
            top: avpAccessTokens.navigation.inset,
            left: avpAccessTokens.navigation.inset,
            zIndex: 2,
            minWidth: avpAccessTokens.navigation.backButtonSize,
            width: avpAccessTokens.navigation.backButtonSize,
            height: avpAccessTokens.navigation.backButtonSize,
            p: 0,
            borderRadius: "50%",
            color: designTokens.fill.text.common100.dark,
            bgcolor: "rgba(255, 255, 255, 0.10)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.15)" },
          }}
        >
          <ChevronLeftIcon />
        </Button>
      )}
      <Box
        sx={{
          minHeight: isNativeAvpWebView
            ? `calc(100dvh - ${avpAccessTokens.navigation.height}px)`
            : "calc(100dvh - 98px)",
          display: isNativeAvpWebView ? "flex" : "grid",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: isNativeAvpWebView ? "flex-start" : undefined,
          placeItems: isNativeAvpWebView ? undefined : "center",
          pt: isNativeAvpWebView ? 3 : 0,
          pb: isNativeAvpWebView ? 8 : "84px",
        }}
      >
        <Stack
          spacing={isNativeAvpWebView ? undefined : 7}
          alignItems="center"
          sx={{
            width: isNativeAvpWebView
              ? `min(${avpAccessTokens.content.width}px, calc(100% - 48px))`
              : "min(512px, 100%)",
            textAlign: "center",
            gap: isNativeAvpWebView ? "28px" : undefined,
          }}
        >
          <Stack spacing={isNativeAvpWebView ? 1.5 : 2.5} alignItems="center">
            {!isNativeAvpWebView && (
              <Typography
                component="h1"
                sx={{ ...designTokens.typography.styles.display1 }}
              >
                {copy.title}
              </Typography>
            )}
            <Typography
              sx={{
                ...(isNativeAvpWebView
                  ? designTokens.typography.styles.body
                  : requestConfirmation
                  ? designTokens.typography.styles.headline1
                  : designTokens.typography.styles.body),
                fontSize: isNativeAvpWebView ? 17 : undefined,
                lineHeight: isNativeAvpWebView ? "normal" : undefined,
                color: designTokens.fill.text.grey90.dark,
                maxWidth: 467,
                whiteSpace: "pre-line",
              }}
            >
              {copy.body}
            </Typography>
          </Stack>

          {requestConfirmation ? (
            <Box
              sx={{
                width: "min(467px, 100%)",
                overflow: "hidden",
                border: `1px solid ${designTokens.stroke.grey50.dark}`,
                borderRadius: designTokens.numbers.cornerRadius[8],
                textAlign: "left",
              }}
            >
              {isNativeAvpWebView && (
                <StatusRow label="Company" value={companyName} compact />
              )}
              <StatusRow
                label="Work email"
                value={email}
                bordered={isNativeAvpWebView}
                compact={isNativeAvpWebView}
              />
              {isNativeAvpWebView && (
                <StatusRow
                  label="Status"
                  value={
                    <StatusChip label="Awaiting approval" tone="pending" />
                  }
                  bordered
                  compact
                />
              )}
            </Box>
          ) : accessState === "approved" ? (
            <Box
              sx={{
                width: "min(467px, 100%)",
                minHeight: 80,
                px: 2,
                py: 2,
                borderRadius: designTokens.numbers.cornerRadius[12],
                bgcolor: designTokens.fill.shape.grey30.dark,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                textAlign: "left",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                minWidth={0}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    overflow: "hidden",
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#1E1E20",
                    color: designTokens.fill.text.common100.dark,
                    fontSize: 20,
                    fontWeight: 500,
                  }}
                >
                  {profilePhotoURL ? (
                    <Box
                      component="img"
                      src={profilePhotoURL}
                      alt={`${email || "Social account"} profile`}
                      referrerPolicy="no-referrer"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    profileInitial
                  )}
                </Box>
                <Stack spacing={0.5} minWidth={0}>
                  {!isSocialLogin && (
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: "16px",
                        color: designTokens.fill.text.grey60.dark,
                      }}
                    >
                      {companyName}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      ...designTokens.typography.styles.label1,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {email}
                  </Typography>
                </Stack>
              </Stack>
              <StatusChip label={copy.chip} tone="approved" />
            </Box>
          ) : accessState === "error" ? null : (
            <Box
              sx={{
                width: "min(467px, 100%)",
                overflow: "hidden",
                border: `1px solid ${designTokens.stroke.grey50.dark}`,
                borderRadius: designTokens.numbers.cornerRadius[8],
                textAlign: "left",
              }}
            >
              {/* 소셜 계정은 Company를 표시하지 않는다. 첫 행에는 구분선을 넣지
                  않으므로 Company가 빠지면 email 행이 첫 행이 된다. */}
              {!isSocialLogin && (
                <StatusRow
                  label="Company"
                  value={companyName}
                  compact={isNativeAvpWebView}
                />
              )}
              <StatusRow
                label={isSocialLogin ? "Email" : "Work email"}
                value={
                  providerIcon ? (
                    // Figma 2295:82008 — 16 아이콘, 8px 뒤에 이메일, 20px 줄에 세로 중앙.
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        minWidth: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={providerIcon.src}
                        alt={providerIcon.alt}
                        sx={{
                          width: 16,
                          height: 16,
                          flexShrink: 0,
                          objectFit: "contain",
                        }}
                      />
                      <Box component="span" sx={{ minWidth: 0 }}>
                        {email}
                      </Box>
                    </Box>
                  ) : (
                    email
                  )
                }
                bordered={!isSocialLogin}
                compact={isNativeAvpWebView}
              />
              <StatusRow
                label="Status"
                value={<StatusChip label={copy.chip} tone={accessState} />}
                bordered
                compact={isNativeAvpWebView}
              />
            </Box>
          )}

          {error && (
            <Typography
              role="alert"
              sx={{
                ...designTokens.typography.styles.body,
                color: designTokens.fill.text.error.dark,
              }}
            >
              {error}
            </Typography>
          )}

          {requiresConsent && status?.policy && (
            <Box sx={{ width: "min(467px, 100%)" }}>
              <StudioPolicyConsent
                id="studio-access-policy-consent"
                checked={policyAccepted}
                onChange={setPolicyAccepted}
                policy={status.policy}
              />
            </Box>
          )}

          <Stack
            spacing={3}
            sx={{
              width: "min(467px, 100%)",
              mt: isNativeAvpWebView ? "20px" : undefined,
            }}
          >
            {requestConfirmation && (
              <Button
                variant="contained"
                disabled={requesting}
                onClick={() => void requestAgain()}
                sx={
                  isNativeAvpWebView
                    ? avpAuthPrimaryActionSx
                    : authPrimaryButtonSx
                }
              >
                {requesting ? "Requesting access" : "Request access"}
              </Button>
            )}
            {!requestConfirmation && accessState === "pending" && (
              <Button
                variant="contained"
                onClick={() =>
                  void refresh({
                    notifyIfPending: true,
                    showApprovedConfirmation: true,
                  })
                }
                sx={
                  isNativeAvpWebView
                    ? avpAuthPrimaryActionSx
                    : authPrimaryButtonSx
                }
              >
                Check approval status
              </Button>
            )}
            {!requestConfirmation && accessState === "rejected" && (
              <Button
                variant="contained"
                onClick={() => void requestAgain()}
                sx={
                  isNativeAvpWebView
                    ? avpAuthPrimaryActionSx
                    : authPrimaryButtonSx
                }
              >
                Request access again
              </Button>
            )}
            {!requestConfirmation && accessState === "approved" && (
              <Button
                variant="contained"
                disabled={requesting || (requiresConsent && !policyAccepted)}
                onClick={() => void continueAfterApproval()}
                sx={
                  isNativeAvpWebView
                    ? avpAuthPrimaryActionSx
                    : authPrimaryButtonSx
                }
              >
                {requesting
                  ? "Signing in"
                  : requiresConsent
                    ? isNativeAvpWebView
                      ? "Sign in to View R"
                      : "Sign in to View R Studio"
                    : isSocialLogin
                      ? isNativeAvpWebView
                        ? "Sign in to View R"
                        : "Sign in to View R Studio"
                      : "Continue"}
              </Button>
            )}
            {requiresConsent && (
              <Button
                variant="text"
                disabled={requesting}
                onClick={returnToLogin}
                sx={{
                  textTransform: "none",
                  color: designTokens.fill.text.grey80.dark,
                }}
              >
                Use a different account
              </Button>
            )}
            {accessState !== "approved" && !isNativeAvpWebView && (
              <Button
                variant="text"
                disabled={requesting}
                onClick={returnToLogin}
                sx={{
                  textTransform: "none",
                  color: designTokens.fill.text.grey80.dark,
                }}
              >
                Back
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
      {isNativeAvpWebView ? (
        <DSDialog
          open={toastOpen}
          onClose={() => setToastOpen(false)}
          aria-labelledby="avp-request-sent-title"
          paperSx={{
            width: `min(${avpAccessTokens.alert.width}px, calc(100vw - 48px))`,
            borderRadius: avpAccessTokens.alert.radius,
            bgcolor: avpAccessTokens.alert.background,
            px: 6,
            py: 5,
            textAlign: "center",
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Typography
              id="avp-request-sent-title"
              component="h2"
              sx={{ fontSize: 28, fontWeight: 700 }}
            >
              Request sent
            </Typography>
            <Typography
              sx={{
                fontSize: 17,
                lineHeight: "22px",
                color: designTokens.fill.text.grey90.dark,
              }}
            >
              The request will be approved soon. Please try again later.
            </Typography>
            <Button
              onClick={() => setToastOpen(false)}
              sx={{ ...avpAuthPrimaryActionSx, width: 240 }}
            >
              OK
            </Button>
          </Stack>
        </DSDialog>
      ) : (
        <Toast
          variant="no-icon"
          message="The request will be approved soon. Please try again later."
          open={toastOpen}
          onClose={() => setToastOpen(false)}
        />
      )}
    </Box>
  );
};

const avpAuthPrimaryActionSx = {
  ...authPrimaryButtonSx,
  width: "100%",
  height: designTokens.semantic.avpAuthentication.login.button.height,
  borderRadius: designTokens.semantic.avpAuthentication.login.button.radius,
  fontSize: designTokens.semantic.avpAuthentication.login.button.fontSize,
} as const;

const StatusRow = ({
  label,
  value,
  bordered = false,
  compact = false,
}: {
  label: string;
  value: ReactNode;
  bordered?: boolean;
  compact?: boolean;
}) => (
  <Box
    sx={{
      minHeight: compact
        ? designTokens.semantic.avpAuthentication.access.row.minHeight
        : 56,
      px: 3,
      display: "flex",
      alignItems: "center",
      gap: 2,
      borderTop: bordered
        ? `1px solid ${designTokens.stroke.grey50.dark}`
        : undefined,
    }}
  >
    <Typography
      sx={{
        width: 120,
        flexShrink: 0,
        fontSize: 17,
        lineHeight: "20px",
        color: designTokens.fill.text.grey60.dark,
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        minWidth: 0,
        color: designTokens.fill.text.grey90.dark,
        fontSize: 17,
        fontWeight: 500,
        lineHeight: "20px",
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Box>
  </Box>
);

const StatusChip = ({
  label,
  tone,
}: {
  label: string;
  tone: "approved" | "pending" | "rejected" | "inactive";
}) => {
  const isPositive = tone === "approved" || tone === "pending";
  return (
    <Box
      sx={{
        height: 28,
        px: 1.5,
        borderRadius: "4px",
        display: "inline-flex",
        alignItems: "center",
        gap: 1.25,
        whiteSpace: "nowrap",
        bgcolor: isPositive
          ? "rgba(80, 183, 134, 0.5)"
          : "rgba(255, 82, 82, 0.16)",
        color: isPositive
          ? designTokens.fill.text.common100.dark
          : designTokens.fill.text.error.dark,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: "16px",
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: isPositive ? "#58d29c" : designTokens.fill.text.error.dark,
        }}
      />
      {label}
    </Box>
  );
};

export default AccessRequestPage;
